import { Autocomplete, AutocompleteProps, AutocompleteValue, TextField, TextFieldProps } from '@mui/material';
import { Control, Controller, FieldValues, Path, RegisterOptions } from 'react-hook-form';

export type AutocompleteControlProps<
  TFieldValues extends FieldValues,
  TOption,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> = {
  name: Path<TFieldValues>;
  control: Control<TFieldValues>;
  id?: string;
  textFieldProps?: TextFieldProps;
  rules?: Omit<RegisterOptions<TFieldValues, Path<TFieldValues>>, 'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'>;
} & Omit<AutocompleteProps<TOption, Multiple, DisableClearable, FreeSolo>, 'renderInput' | 'options' | 'onChange' | 'value' | 'name'> & {
    options: AutocompleteProps<TOption, Multiple, DisableClearable, FreeSolo>['options'];
    renderInput?: AutocompleteProps<TOption, Multiple, DisableClearable, FreeSolo>['renderInput'];
  };

export function AutocompleteControl<
  TFieldValues extends FieldValues,
  TOption,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>({
  name,
  control,
  rules,
  id,
  textFieldProps,
  options,
  renderInput,
  ...autocompleteProps
}: AutocompleteControlProps<TFieldValues, TOption, Multiple, DisableClearable, FreeSolo>) {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => {
        let value: AutocompleteValue<TOption, Multiple, DisableClearable, FreeSolo>;

        if (autocompleteProps.multiple) {
          value = (field.value ?? []) as AutocompleteValue<TOption, Multiple, DisableClearable, FreeSolo>;
        } else {
          value = (field.value ?? null) as AutocompleteValue<TOption, Multiple, DisableClearable, FreeSolo>;
        }

        return (
          <Autocomplete
            {...autocompleteProps}
            {...field}
            options={options}
            value={value}
            onChange={(_, newValue) => field.onChange(newValue)}
            onInputChange={(_, newValue) => {
              if (autocompleteProps.freeSolo) field.onChange(newValue);
            }}
            renderInput={
              renderInput ??
              (params => (
                <TextField
                  {...params}
                  {...textFieldProps}
                  id={id}
                  variant="outlined"
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              ))
            }
          />
        );
      }}
    />
  );
}
